/**
 * API Service for Refi Dashboard
 * Handles all backend API calls
 */

import axios from 'axios';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://csv8ddssyf.execute-api.us-east-2.amazonaws.com';
const PORTAL_URL = 'https://xzudlhkhzj.execute-api.us-east-2.amazonaws.com/prod/portal';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add QLS session to all requests
apiClient.interceptors.request.use((config) => {
  const sessionId = sessionStorage.getItem('qls_session');
  
  console.log('🔧 API Interceptor - Session ID from storage:', sessionId);
  console.log('🔧 API Interceptor - Request URL:', config.url);
  
  if (sessionId) {
    // Add session to query params
    if (!config.params) {
      config.params = {};
    }
    config.params.qls_session = sessionId;
    
    console.log('✅ Added qls_session to request params:', config.params);
    
    // Also add to body for POST requests
    if (config.method === 'post' && config.data) {
      config.data = {
        ...config.data,
        qls_session: sessionId
      };
      console.log('✅ Added qls_session to POST body');
    }
  } else {
    console.warn('⚠️ No session ID in sessionStorage!');
  }
  
  return config;
});

// Handle response errors
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error);
    console.error('❌ Error Response:', error.response);
    console.error('❌ Error Status:', error.response?.status);
    console.error('❌ Error Data:', error.response?.data);
    
    if (error.response?.status === 401) {
      console.warn('⚠️ 401 Unauthorized - Session expired, redirecting to portal');
      
      // Get session ID before clearing (in case we can pass it to portal)
      const sessionId = sessionStorage.getItem('qls_session');
      
      // Clear session storage
      sessionStorage.removeItem('qls_session');
      sessionStorage.removeItem('user_email');
      sessionStorage.removeItem('user_name');
      sessionStorage.removeItem('user_role');
      
      alert('Your session has expired. Please log in again.');
      
      // Redirect to portal (with session if available, for graceful re-login)
      if (sessionId) {
        window.location.href = `${PORTAL_URL}?qls_session=${sessionId}`;
      } else {
        window.location.href = PORTAL_URL;
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get refi processing history
 * @param {Object} params - Query parameters
 * @param {number} params.days - Number of days to look back (default: 30)
 * @param {string} params.status - Filter by status (success, failed, pending)
 * @returns {Promise} API response
 */
export const getRefiHistory = async (params = {}) => {
  try {
    const response = await apiClient.get('/history', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching refi history:', error);
    throw error;
  }
};

/**
 * Get current feature flags
 * @returns {Promise} API response
 */
export const getFeatureFlags = async () => {
  try {
    const response = await apiClient.get('/feature-flags');
    return response.data;
  } catch (error) {
    console.error('Error fetching feature flags:', error);
    throw error;
  }
};

/**
 * List CSV files
 * @param {Object} params - Query parameters
 * @param {string} params.date - Filter by date (YYYY-MM-DD)
 * @returns {Promise} API response
 */
export const listCSVFiles = async (params = {}) => {
  try {
    const response = await apiClient.get('/csvs', { params });
    return response.data;
  } catch (error) {
    console.error('Error listing CSV files:', error);
    throw error;
  }
};

/**
 * Generate download URL for a CSV file
 * @param {string} key - S3 key for the CSV file
 * @returns {Promise} API response with download URL
 */
export const getCSVDownloadURL = async (key) => {
  try {
    const response = await apiClient.get('/csv/download', {
      params: { key }
    });
    return response.data;
  } catch (error) {
    console.error('Error generating download URL:', error);
    throw error;
  }
};

/**
 * Manually trigger refi accounting for a single loan pair
 * @param {Object} refiData - Refi data
 * @param {number} refiData.new_loan_id - New loan ID
 * @param {string} refiData.old_loan_number - Old loan number
 * @param {number} refiData.refi_payoff - Refi payoff amount
 * @param {string} refiData.refi_party - Refi party name
 * @param {Object} refiData.knackly_data - Full Knackly data payload
 * @returns {Promise} API response
 */
export const triggerRefiAccounting = async (refiData) => {
  try {
    const response = await apiClient.post('/refi/trigger', refiData);
    return response.data;
  } catch (error) {
    console.error('Error triggering refi accounting:', error);
    throw error;
  }
};

/**
 * Bulk trigger refi accounting for multiple loan pairs
 * @param {Array} refis - Array of refi data objects
 * @returns {Promise} API response
 */
export const bulkTriggerRefiAccounting = async (refis) => {
  try {
    const response = await apiClient.post('/refi/bulk-trigger', { refis });
    return response.data;
  } catch (error) {
    console.error('Error bulk triggering refi accounting:', error);
    throw error;
  }
};

/**
 * Execute refi accounting for a previously previewed refi
 * @param {string} s3_summary_key - S3 key for the test summary JSON
 * @returns {Promise} API response
 */
export const executeRefiAccounting = async (s3_summary_key) => {
  try {
    console.log('🚀 Executing refi accounting for S3 key:', s3_summary_key);
    const response = await apiClient.post('/refi/execute', { s3_summary_key });
    console.log('✅ Execute response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error executing refi accounting:', error);
    throw error;
  }
};

/**
 * Delete a pending refi record (S3 + DynamoDB)
 * @param {string} s3_summary_key - S3 key for the test summary JSON
 * @returns {Promise} API response
 */
export const deleteRefi = async (s3_summary_key) => {
  try {
    console.log('🗑️ Deleting refi record for S3 key:', s3_summary_key);
    const response = await apiClient.post('/refi/delete', { s3_summary_key });
    console.log('✅ Delete response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting refi record:', error);
    throw error;
  }
};

/**
 * Download all CSV files for a specific date as a ZIP
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise} ZIP file blob
 */
export const downloadCSVsAsZip = async (date) => {
  try {
    console.log('📦 Downloading ZIP for date:', date);
    const response = await apiClient.get('/csv/download-zip', {
      params: { date },
      responseType: 'blob' // Important for binary data
    });
    console.log('✅ ZIP downloaded successfully');
    return response.data;
  } catch (error) {
    console.error('❌ Error downloading ZIP:', error);
    throw error;
  }
};

export default apiClient;

